import type { AuthAuthAuthGetParams } from '@/shared/generated'
import type { ErrorComponentProps } from '@tanstack/react-router'

import {
	createFileRoute,
	Link,
	redirect,
	SearchParamError,
} from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { AlertTriangleIcon } from 'lucide-react'
import z from 'zod'

import { SESSION_USER_QUERY_KEY } from '@/entities/session'

import { client } from '@/shared/api/client'
import { FullscreenLoading } from '@/shared/components/common/FullscreenLoading'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { setAccessToken } from '@/shared/lib/auth/token-storage'
import { getAuthenticationCallbackUrl } from '@/shared/lib/auth/utils'

const validateSearch = z.object({
	code: z.string(),
	session_state: z.string(),
	error: z.string().optional(),
})

const getErrorMessage = (error: unknown) => {
	if (error instanceof AxiosError) {
		return 'The code is invalid or has expired. Please try again.'
	}
	if (error instanceof SearchParamError) {
		return 'The search parameters are invalid. Please contact your administrator or support team for more information.'
	}

	return 'An unknown error occurred. Please try again.'
}
const ErrorComponent = ({ error }: ErrorComponentProps) => {
	const errorMessage = getErrorMessage(error)

	return (
		<Empty className='h-screen w-screen'>
			<EmptyHeader>
				<EmptyMedia variant='icon' className='bg-orange-500/20'>
					<AlertTriangleIcon className='text-orange-500' />
				</EmptyMedia>
				<EmptyTitle className='text-xl font-bold'>
					Authentication failed
				</EmptyTitle>
				<EmptyDescription>{errorMessage}</EmptyDescription>
			</EmptyHeader>
			<EmptyContent>
				<Link to='/'>Try again</Link>
			</EmptyContent>
		</Empty>
	)
}

export const Route = createFileRoute('/authenticate/')({
	pendingComponent: () => <FullscreenLoading />,
	loaderDeps: ({ search: { code, session_state } }) => ({
		code,
		session_state,
	}),
	loader: async ({ context: { queryClient }, deps }) => {
		const data = await client.auth.authAuthAuthGet({
			...deps,
			redirect_uri: getAuthenticationCallbackUrl(),
		} as AuthAuthAuthGetParams)
		setAccessToken(data.access_token)
		// Drop the signed-out entry so the `/_app` guard refetches with the new token.
		await queryClient.invalidateQueries({ queryKey: SESSION_USER_QUERY_KEY })
		throw redirect({ to: '/', replace: true })
	},
	errorComponent: ErrorComponent,
	validateSearch,
})
